import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { API_CONFIG } from '@core/config/api.config';
import { AuthService } from './auth.service';
import { SKIP_AUTH_INTERCEPTOR } from './auth.context';

/**
 * Adjunta Bearer token y ante 401 intenta un refresh único + reintento.
 * Si el refresh falla, limpia sesión y redirige a /login.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SKIP_AUTH_INTERCEPTOR) || API_CONFIG.useMockAuth) {
    return next(req);
  }

  const auth = inject(AuthService);
  const router = inject(Router);
  const token = auth.getToken();

  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401 || !token) {
        return throwError(() => error);
      }

      // Evitar bucle si la petición ya es de auth token
      if (isAuthTokenUrl(authReq.url)) {
        return throwError(() => error);
      }

      return from(auth.refreshSession({ force: true })).pipe(
        switchMap((newToken) => {
          if (!newToken) {
            void forceLogout(auth, router);
            return throwError(() => error);
          }

          const retry = authReq.clone({
            setHeaders: { Authorization: `Bearer ${newToken}` }
          });
          return next(retry);
        })
      );
    })
  );
};

function isAuthTokenUrl(url: string): boolean {
  return (
    url.includes('/autenticacion/login') ||
    url.includes('/autenticacion/validar-token') ||
    url.includes('/autenticacion/refrescar-token') ||
    url.includes('/autenticacion/eliminar-token') ||
    url.includes('/recuperacion/') ||
    url.includes('/usuarios/activar-usuario')
  );
}

async function forceLogout(auth: AuthService, router: Router): Promise<void> {
  auth.clearLocalSession();
  await router.navigateByUrl('/login', { replaceUrl: true });
}
